import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
// icons
import { AiOutlinePlus } from 'react-icons/ai';
import { formatCurrency } from 'utils/currencyFormatter';
// contants
import { getOrderStatus, getPaymentMethod, getPaymentStatus } from '../../../constants';
// api
import { getShippingProviders } from 'api/transport';
// components
import TextArea from 'components/backoffice/customInput/textAreaCustom';
import SearchCompanyInput from 'components/backoffice/customInput/SearchCompanyInput';
import SearchUserInput from 'components/backoffice/customInput/SearchUserInput';
import Select from 'components/backoffice/selects/SelectCustom';
import Subtitle from 'components/backoffice/texts/subtitle';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import Button from 'components/backoffice/buttonCustom';
import Input from 'components/backoffice/customInput';
import ArticlesHeader from 'components/backoffice/headers/articles';
import ProductOrder from './productOrder';
import Albaranes from 'components/backoffice/orderDocuments/Albaranes';
import Facturas from 'components/backoffice/orderDocuments/Facturas';
import AdjuntarAlbaran from 'components/backoffice/orderDocuments/AdjuntarAlbaran';
import AdjuntarFactura from 'components/backoffice/orderDocuments/AdjuntarFactura';
import SaveIcon from 'components/backoffice/icons/SaveIcon';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';
import { convertDateToDDMMYYYY } from 'helpers';

const MAX_ORDER_DOCUMENT_SIZE = 10 * 1024 * 1024;

const formatOrderDocumentSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const createLocalOrderDocument = (file) => {
  const date = convertDateToDDMMYYYY(new Date());
  const size = formatOrderDocumentSize(file.size);

  return {
    id: `${file.name}-${Date.now()}-${Math.random()}`,
    name: file.name,
    size,
    date,
    meta: `${date} · ${size}`,
    url: URL.createObjectURL(file),
    file,
  };
};

const OrderForm = ({ onSubmit, data = {}, isEditing, loading }) => {
  const { t } = useTranslation(['backoffice', 'contact', 'shipping', 'register']);
  const navigate = useNavigate();
  const [shippingProviders, setShippingProviders] = useState([]);
  const [albaranes, setAlbaranes] = useState([]);
  const [facturas, setFacturas] = useState([]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedFields = watch();
  const descuentoValue = watch('descuento');
  const previousDescuentoRef = useRef(descuentoValue || '0');

  useEffect(() => {
    const loadShippingProviders = async () => {
      try {
        const response = await getShippingProviders();
        if (response?.status === 200 && response?.data) {
          const providers = Array.isArray(response.data) ? response.data : response.data.results || [];
          console.log('providers', providers);
          const formattedProviders = providers.map((provider) => ({
            id: provider.id,
            value: provider.name || provider.nombre || provider.label,
          }));
          console.log('formattedProviders', formattedProviders);
          setShippingProviders(formattedProviders);
        }
      } catch (error) {
        console.error('Error loading shipping providers:', error);
      }
    };
    loadShippingProviders();
  }, []);

  useEffect(() => {
    if (Object.keys(data).length > 0) {
      setValue('usuario', data.user || null);
      setValue('benefited_company', data.benefited_company || null);
      setValue('descuento', data.descuento);
      setValue('comentario', data.comentario);
      setValue('reembolso', data.reembolso);
      setValue('order_status', data.order_status);
      setValue('payment_method', data.payment_method);
      setValue('payment_status', data.payment_status);
      setValue('commission_amount', data.commission_amount);
      setValue('commission_for_instore_pickup', data.commission_for_instore_pickup);
      setValue('commission_status_display', data.commission_status_display);
      setValue('shipping_commission', data.shipping_commission);
      setValue('transport_company', data.transport_company?.id || null);
      setValue('tracking_id', data.tracking_id || '');
      data?.articles?.forEach((item) => {
        append({
          product: item.product,
          discount: item.descuento,
          editorial: item.product.editorial,
          price: parseFloat(item.precio_venta).toFixed(2),
          total_price: item.total,
          quantity: item.quantity,
          state: item.estado,
          book_condition: item.book_condition ?? item.bookCondition,
        });
      });
      previousDescuentoRef.current = data.descuento || '0';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (descuentoValue !== undefined && descuentoValue !== null && watchedFields.items && watchedFields.items.length > 0) {
      const currentDescuento = descuentoValue || '0';
      const previousDescuento = previousDescuentoRef.current || '0';
      
      if (currentDescuento !== previousDescuento) {
        watchedFields.items.forEach((item, index) => {
          setValue(`items.${index}.discount`, currentDescuento);
        });
        previousDescuentoRef.current = currentDescuento;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descuentoValue]);

  const orderStatus = getOrderStatus(t);

  const paymentMethod = getPaymentMethod(t);

  const paymentStatus = getPaymentStatus(t);

  const onAddProduct = () => {
    const defaultDiscount = descuentoValue || '0';
    append({ product: null, discount: defaultDiscount, editorial: '', price: 0, total_price: 0, quantity: 1, state: 0, book_condition: undefined });
  };

  useEffect(() => {
    return () => {
      [...albaranes, ...facturas].forEach((doc) => {
        if (doc?.url) URL.revokeObjectURL(doc.url);
      });
    };
  }, [albaranes, facturas]);

  const addLocalDocuments = (setDocuments, files) => {
    const validFiles = files.filter((file) => file.size <= MAX_ORDER_DOCUMENT_SIZE);
    if (!validFiles.length) return;

    setDocuments((prev) => [...prev, ...validFiles.map(createLocalOrderDocument)]);
  };

  const removeLocalDocument = (setDocuments, id) => {
    setDocuments((prev) => {
      const doc = prev.find((item) => item.id === id);
      if (doc?.url) URL.revokeObjectURL(doc.url);
      return prev.filter((item) => item.id !== id);
    });
  };

  const viewLocalDocument = (doc) => {
    if (doc?.url) window.open(doc.url, '_blank', 'noopener,noreferrer');
  };

  const downloadLocalDocument = (doc) => {
    if (!doc?.url) return;

    const link = document.createElement('a');
    link.href = doc.url;
    link.download = doc.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="mt-5">
      {isEditing && (
        <Row className="mb-3">
        {/*   <Col sm={12} md={12} lg={12} className="mb-4 d-flex flex-column">
            <div className="d-flex flex-row flex-wrap border-grey p-3 h-100 row-gap-3">
              <div className="d-flex store-pickup-row">
                <span className="text-light fw-bold contact-label">{`${t('product.quantity')}: `}</span>
                <span className="text-light">{formatCurrency(Number(data.commission_amount) || 0)}</span>
              </div>
              <div className="d-flex store-pickup-row">
                <span className="text-light fw-bold contact-label">{`${t('instorePickup')}: `}</span>
                <span className="text-light">{data.commission_for_instore_pickup || '0'}%</span>
              </div>
              {data.shipping_commission !== 0 && (
                <div className="d-flex store-pickup-row">
                  <span className="text-light fw-bold contact-label">{`${t('shippingCommission')}: `}</span>
                  <span className="text-light">{formatCurrency(data.shipping_commission || 0)}</span>
                </div>
              )}
              <div className="d-flex store-pickup-row">
                <span className="text-light fw-bold contact-label">{`${t('state')}: `}</span>
                <span className="text-light">{data.commission_status_display || t('notDefined')}</span>
              </div>
            </div>
          </Col> */}
          <Col sm={12} md={12} lg={12} className="mb-4 d-flex flex-column">
            <div className="d-flex flex-row flex-wrap border-grey store-pickup-box p-3 h-100 row-gap-3">
              <div className="d-flex store-pickup-row">
                <span className="text-light fw-bold contact-label">{`${t('instorePickup')}: `}</span>
                <span className="text-light">{data?.store_pickup ? t('si') : t('no')}</span>
              </div>
              {data?.store_pickup && data?.bookstore_to_pickup_selected && (
                <>
                  <div className="d-flex store-pickup-row">
                    <span className="text-light fw-bold contact-label">{`${t('orderTrans.pickupLibrary')}: `}</span>
                    <span className="text-light">
                      {data?.bookstore_to_pickup_selected?.name || '-'}
                    </span>
                  </div>
                  {data?.bookstore_to_pickup_selected?.direccion && (
                    <div className="d-flex store-pickup-row">
                      <span className="text-light fw-bold contact-label">{`${t('address', { ns: 'shipping' })}: `}</span>
                      <span className="text-light me-3">
                        {data?.bookstore_to_pickup_selected?.direccion}
                      </span>
                    </div>
                  )}
                  {data?.bookstore_to_pickup_selected?.email && (
                    <div className="d-flex store-pickup-row">
                      <span className="text-light fw-bold contact-label">{`${t('correoelectronico')}: `}</span>
                      <span className="text-light">
                        {data?.bookstore_to_pickup_selected?.email}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </Col>
          <Col sm={12} md={12} lg={12} className="mb-4 d-flex flex-column">
            <Form.Label className="text-light">{t('contact', { ns: 'contact' })}</Form.Label>
            <div className="d-flex flex-row flex-wrap border-grey store-pickup-box p-3 h-100 row-gap-3">
              {data?.contact_info && Object.keys(data?.contact_info).length > 0 && (
                <>
                  <div className="d-flex contact-row">
                    <span className="text-light fw-bold contact-label">{`${t('name')}: `}</span>
                    <span className="text-light">{data?.contact_info?.name || ''}</span>
                  </div>
                  <div className="d-flex contact-row">
                    <span className="text-light fw-bold contact-label">{`${t('correoelectronico')}: `}</span>
                    <span className="text-light">{data?.contact_info?.email || ''}</span>
                  </div>
                  {(data?.contact_info?.phone || data?.contact_info?.telefono || data?.shipping_address?.phone || data?.shipping_address?.telefono) && (
                    <div className="d-flex contact-row">
                      <span className="text-light fw-bold contact-label">{`${t('telefono')}: `}</span>
                      <span className="text-light">{data?.contact_info?.phone || data?.contact_info?.telefono || data?.shipping_address?.phone || data?.shipping_address?.telefono || data?.billing_address?.phone || data?.billing_address?.telefono || ''}</span>
                    </div>
                  )}
                </>
              )}
              <div className="d-flex contact-row">
                <span className="text-light fw-bold contact-label">{`${t('shippingaddress', { ns: 'register' })}: `}</span>
                <span className="text-light me-3">
                  {data?.shipping_address?.address ? [
                    data?.shipping_address?.address,
                    data?.shipping_address?.locality,
                    data?.shipping_address?.province?.label || data?.shipping_address?.province?.nombre || '',
                    data?.shipping_address?.postal_code,
                    data?.shipping_address?.country?.label || data?.shipping_address?.country?.nombre || ''
                  ].filter(Boolean).join(', ') : '-'}
                </span>
              </div>
              <div className="d-flex contact-row">
                <span className="text-light fw-bold contact-label">{`${t('orderTrans.billingAddress')}: `}</span>
                <span className="text-light me-3">
                  {data?.billing_address?.address ? [
                    data?.billing_address?.address,
                    data?.billing_address?.locality,
                    data?.billing_address?.province?.label || data?.billing_address?.province?.nombre || '',
                    data?.billing_address?.postal_code,
                    data?.billing_address?.country?.label || data?.billing_address?.country?.nombre || ''
                  ].filter(Boolean).join(', ') : '-'}
                </span>
              </div>
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="usuario"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <div className="w-100 orders" data-testid="autocomplete_user">
                <SearchUserInput onSelected={(item) => onChange(item)} defaultUser={value} />
              </div>
            )}
          />
          {errors.usuario && <small className="text-danger ">{errors.usuario.message}</small>}
        </Col>
        {/* Campo benefited_company temporalmente deshabilitado
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="benefited_company"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <div className="w-100 orders" data-testid="autocomplete_company">
                <SearchCompanyInput onSelected={(item) => onChange(item)} defaultCompany={value} label={t('benefitedbookstore')}/>
              </div>
            )}
          />
          {errors.benefited_company && <small className="text-danger ">{errors.benefited_company.message}</small>}
        </Col>
        */}
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="order_status"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                label={t('orderTrans.orderStatus')}
                placeholder={t('orderTrans.selectOrderStatus')}
                choices={orderStatus}
                value={value}
                onCheckedItem={onChange}
              />
            )}
          />
          {errors.order_status && <small className="text-danger ">{errors.order_status.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="payment_method"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                label={t('orderTrans.paymentMethod')}
                placeholder={t('orderTrans.selectPaymentMethod')}
                choices={paymentMethod}
                value={value}
                onCheckedItem={onChange}
              />
            )}
          />
          {errors.payment_method && <small className="text-danger ">{errors.payment_method.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="payment_status"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                label={t('orderTrans.paymentStatus')}
                placeholder={t('orderTrans.selectPaymentStatus')}
                choices={paymentStatus}
                value={value}
                onCheckedItem={onChange}
              />
            )}
          />
          {errors.payment_status && <small className="text-danger ">{errors.payment_status.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="descuento"
            control={control}
            defaultValue="0"
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <div className="w-100">
                <Input
                  type={'number'}
                  label={t('product.discount')}
                  placeholder="%"
                  value={value}
                  onChange={onChange}
                />
              </div>
            )}
          />
          {errors.descuento && <small className="text-danger ">{errors.descuento.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="transport_company"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                label={t('orderTrans.shippingProvider')}
                placeholder={t('orderTrans.selectShippingProvider')}
                choices={shippingProviders}
                value={value}
                onCheckedItem={onChange}
              />
            )}
          />
          {errors.transport_company && <small className="text-danger ">{errors.transport_company.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="tracking_id"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div className="w-100">
                <Input
                  type={'text'}
                  label={t('orderTrans.trackingId')}
                  placeholder={t('orderTrans.trackingIdPlaceholder')}
                  value={value}
                  onChange={onChange}
                />
              </div>
            )}
          />
          {errors.tracking_id && <small className="text-danger ">{errors.tracking_id.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4 align-self-start mt-2">
          <Controller
            name="reembolso"
            control={control}
            render={({ field }) => (
              <CustomCheckbox
                label={t('orderTrans.refund')}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          {errors.reembolso && <small className="text-danger ">{errors.reembolso.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="comentario"
            control={control}
            render={({ field }) => <TextArea label={t('comment')} placeholder={t('comment')} {...field} />}
          />
          {errors.comentario && <small className="text-danger ">{errors.comentario.message}</small>}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Subtitle text={t('products')} />
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-end pb-4">
          <Button color="terciary" onClick={onAddProduct} type={'button'}>
            {t('product.addProduct')} <AiOutlinePlus />
          </Button>
        </Col>
        <ArticlesHeader includeProductBookStatus lineItems={watchedFields?.items} />
      </Row>
      <div data-testid="productOrderForm">
        {fields.map((field, index) => (
          <ProductOrder
            key={index}
            id={index}
            article={field}
            onDelete={() => remove(index)}
            control={control}
            setValue={setValue}
            watchedFields={watchedFields}
          />
        ))}
      </div>
      {isEditing && (
        <>
          <div className="mt-5">
            <Albaranes
              documents={albaranes}
              onView={viewLocalDocument}
              onDownload={downloadLocalDocument}
              onDelete={(id) => removeLocalDocument(setAlbaranes, id)}
            />
          </div>
          <div className="mt-5">
            <Facturas
              documents={facturas}
              onView={viewLocalDocument}
              onDownload={downloadLocalDocument}
              onDelete={(id) => removeLocalDocument(setFacturas, id)}
            />
          </div>
          <div className="mt-5">
            <AdjuntarAlbaran onFilesSelected={(files) => addLocalDocuments(setAlbaranes, files)} />
          </div>
          <div className="mt-5">
            <AdjuntarFactura onFilesSelected={(files) => addLocalDocuments(setFacturas, files)} />
          </div>
        </>
      )}
      <div className="my-4 d-flex justify-content-end gap-3">
        <Button color="terciary" onClick={() => navigate(-1)} type={'button'}>
          {t('cancel')}
        </Button>

        <Button color="primary" onClick={handleSubmit(onSubmit)} type={'submit'} data-testid="save">
          {t('save')}
          {loading ? <SpinnerComponent loading={loading} /> : <SaveIcon />}
        </Button>
      </div>
    </div>
  );
};

OrderForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
};

export default OrderForm;
